var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import moment from "moment-timezone";
import { useSessionContext } from "./SessionContext";
export var WebSocketContext = createContext({
    connect: function () { },
    setMessage: function () { },
    ready: false,
    message: null,
    instantChatMessage: null,
    send: function () { },
    disconnect: function () { },
    connectionManager: undefined
});
export function useWebSocket() {
    return useContext(WebSocketContext);
}
export var WebSocketProvider = function (_a) {
    var _b;
    var children = _a.children, _c = _a.autoConnect, autoConnect = _c === void 0 ? false : _c;
    var _d = useState(false), isReady = _d[0], setIsReady = _d[1];
    var _e = useSessionContext(), token = _e.token, user = _e.user, subdomain = _e.subdomain, zoomContext = _e.zoomContext;
    console.log("WebSocketProvider::zoomContext", zoomContext);
    var _f = useState(null), val = _f[0], setVal = _f[1];
    var _g = useState(null), instantChatMessage = _g[0], setInstantChatMessage = _g[1];
    var ws = useRef(null);
    var reconnectAttempts = useRef(0);
    var reconnectTimeoutId = useRef();
    var heartbeatIntervalId = useRef();
    var pingTimeoutId = useRef();
    var isAlive = useRef(true);
    var activeMeetingIdRef = useRef(null);
    var shouldMaintainConnectionRef = useRef(false);
    var getReconnectDelay = function () {
        var baseDelay = 1000;
        var maxDelay = 30000;
        var delay = Math.min(baseDelay * Math.pow(1.5, reconnectAttempts.current), maxDelay);
        return delay;
    };
    var disconnect = useCallback(function (options) {
        var _a;
        var permanent = (_a = options === null || options === void 0 ? void 0 : options.permanent) !== null && _a !== void 0 ? _a : false;
        if (permanent) {
            shouldMaintainConnectionRef.current = false;
            activeMeetingIdRef.current = null;
            reconnectAttempts.current = 0;
        }
        if (ws.current) {
            console.log('ZOOM:: disconnecting websocket connection');
            ws.current.close();
            ws.current = null;
            setIsReady(false);
        }
        // Clear all timeouts and intervals
        if (reconnectTimeoutId.current) {
            clearTimeout(reconnectTimeoutId.current);
        }
        if (heartbeatIntervalId.current) {
            clearInterval(heartbeatIntervalId.current);
        }
        if (pingTimeoutId.current) {
            clearTimeout(pingTimeoutId.current);
        }
    }, []);
    var setMessage = function (msg) {
        setVal(msg);
    };
    // Reset the ping timeout
    var heartbeat = useCallback(function () {
        isAlive.current = true;
        if (pingTimeoutId.current) {
            clearTimeout(pingTimeoutId.current);
        }
        // If no heartbeat received within 30 seconds, consider the connection dead
        pingTimeoutId.current = setTimeout(function () {
            var _a;
            if (!isAlive.current && ((_a = ws.current) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN) {
                console.log('ZOOM:: Connection dead - no heartbeat received');
                disconnect();
            }
        }, 30000);
    }, []);
    // Setup heartbeat mechanism
    var setupHeartbeat = useCallback(function () {
        if (heartbeatIntervalId.current) {
            clearInterval(heartbeatIntervalId.current);
        }
        heartbeat(); // Initial heartbeat
        // Send ping every 15 seconds
        heartbeatIntervalId.current = setInterval(function () {
            var _a;
            if (((_a = ws.current) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN) {
                isAlive.current = false;
                try {
                    ws.current.send(JSON.stringify({ type: 'ping' }));
                }
                catch (error) {
                    console.error('ZOOM:: Failed to send ping:', error);
                }
            }
        }, 15000);
    }, [heartbeat]);
    var connect = useCallback(function (botId) {
        var _a, _b, _c;
        if (((_a = ws.current) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN) {
            console.log('ZOOM:: already connected');
            return;
        }
        if (((_b = ws.current) === null || _b === void 0 ? void 0 : _b.readyState) === WebSocket.CONNECTING) {
            console.log('ZOOM:: connection in progress');
            return;
        }
        var meetingId = ((_c = zoomContext === null || zoomContext === void 0 ? void 0 : zoomContext.recallBot) === null || _c === void 0 ? void 0 : _c.id) || botId || activeMeetingIdRef.current;
        if (!meetingId) {
            console.log('ZOOM:: botID not found');
            return;
        }
        activeMeetingIdRef.current = meetingId;
        shouldMaintainConnectionRef.current = true;
        if (reconnectTimeoutId.current) {
            clearTimeout(reconnectTimeoutId.current);
        }
        console.log('ZOOM:: connecting websocket', process.env.REACT_APP_WEBSOCKET_URL);
        var socket = new WebSocket("".concat(process.env.REACT_APP_WEBSOCKET_URL, "?Authorization=").concat(token, "&client=zoom&meeting_id=").concat(meetingId).concat(subdomain ? "&domain=".concat(subdomain) : "&domain=HitWit-public"));
        socket.onopen = function () {
            console.log('ZOOM:: WebSocket connection established');
            setIsReady(true);
            reconnectAttempts.current = 0;
            setupHeartbeat();
        };
        socket.onclose = function (event) {
            console.log('ZOOM:: WebSocket connection closed', event.code, event.reason);
            ws.current = null;
            setIsReady(false);
            // Clear heartbeat related timeouts
            if (heartbeatIntervalId.current)
                clearInterval(heartbeatIntervalId.current);
            if (pingTimeoutId.current)
                clearTimeout(pingTimeoutId.current);
            if (shouldMaintainConnectionRef.current && activeMeetingIdRef.current) {
                var delay = reconnectAttempts.current === 0 ? 0 : getReconnectDelay();
                console.log("ZOOM:: Connection closed. Attempting reconnect in ".concat(delay, "ms"));
                reconnectAttempts.current++;
                reconnectTimeoutId.current = setTimeout(function () {
                    connect(activeMeetingIdRef.current || undefined);
                }, delay);
            }
            else {
                console.log('ZOOM:: Reconnection skipped - maintainConnection disabled or meeting id missing');
            }
        };
        socket.onmessage = function (event) {
            try {
                var msg = JSON.parse(event.data);
                // Handle pong messages for connection health check
                if (msg.wsping === 'pong') {
                    isAlive.current = true;
                    heartbeat();
                    return;
                }
                setVal(JSON.stringify(__assign(__assign({}, msg), { ts: moment().format() })));
            }
            catch (e) {
                console.error('ZOOM:: Failed to parse message:', e);
                return;
            }
        };
        socket.onerror = function (error) {
            console.error('ZOOM:: WebSocket error:', error);
        };
        ws.current = socket;
    }, [token, (_b = zoomContext === null || zoomContext === void 0 ? void 0 : zoomContext.recallBot) === null || _b === void 0 ? void 0 : _b.id, setupHeartbeat, heartbeat, subdomain]);
    useEffect(function () {
        // Only connect automatically if autoConnect is true
        if (autoConnect) {
            connect();
        }
        return function () {
            console.log('ZOOM:: WebSocketProvider unmounting');
            disconnect({ permanent: true });
        };
    }, [connect, disconnect, autoConnect]);
    useEffect(function () {
        var handleVisibilityChange = function () {
            if (document.visibilityState === 'visible' && (!ws.current || ws.current.readyState !== WebSocket.OPEN)) {
                // Only reconnect automatically if we were previously connected
                if (isReady) {
                    connect();
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return function () { return document.removeEventListener('visibilitychange', handleVisibilityChange); };
    }, [connect, isReady]);
    useEffect(function () {
        var handleUnload = function () { return disconnect({ permanent: true }); };
        window.addEventListener("unload", handleUnload);
        return function () { return window.removeEventListener("unload", handleUnload); };
    }, [disconnect]);
    var handleLocalSend = function (payload) {
        var _a;
        var payloadToSend = JSON.stringify(__assign(__assign(__assign(__assign(__assign({}, JSON.parse(payload)), (!(payload === null || payload === void 0 ? void 0 : payload.user_timezone) ? { "user_timezone": moment.tz.guess() } : {})), { time_in_session: 1, client: 'zoom' }), (subdomain ? { "X-Domain": subdomain } : { "X-Domain": 'HitWit-public' })), (subdomain ? { "domain": subdomain } : { "domain": 'HitWit-public' })));
        if (((_a = ws.current) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN) {
            console.log("ZOOM:: Sending..", JSON.parse(payloadToSend));
            ws.current.send(payloadToSend);
        }
        else {
            console.log("ZOOM:: Not connected");
            if (!ws.current || ws.current.readyState === WebSocket.CLOSED) {
                connect();
            }
        }
    };
    var contextProvider = {
        setMessage: setMessage,
        connect: connect,
        ready: isReady,
        message: val,
        instantChatMessage: instantChatMessage,
        send: handleLocalSend,
        disconnect: disconnect,
        connectionManager: undefined
    };
    return (_jsx(WebSocketContext.Provider, { value: contextProvider, children: children }));
};
